import { useEffect, useState, type ReactNode } from "react";
import { Box, Button, Card, CardActionArea, CardContent, MenuItem, Stack, TextField, Typography } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import { useAuth, type AuthTenant } from "../api/auth-context";

/**
 * Bloqueia o app até o usuário ter uma instituição selecionada.
 * - 1 tenant disponível → auto-seleciona (backend faz isso, mas reforça aqui).
 * - localStorage `safrater_last_tenant_<userId>` é restaurado automaticamente.
 * - Se nada match: mostra UI de seleção (cards p/ poucos, dropdown p/ master c/ muitos).
 */
const STORAGE_PREFIX = "safrater_last_tenant_";

export function TenantGate({ children }: { children: ReactNode }) {
  const { user, tenants, switchTenant } = useAuth();
  const [pickerValue, setPickerValue] = useState<string>("");
  const [busy, setBusy] = useState(false);

  // Restaura última escolha do localStorage assim que detectar usuário sem tenant.
  useEffect(() => {
    if (!user || user.selectedTenantId || tenants.length === 0) return;
    const saved = localStorage.getItem(`${STORAGE_PREFIX}${user.sub}`);
    if (saved && tenants.some((t) => t.id === saved)) {
      void switchTenant(saved);
    }
  }, [user, tenants, switchTenant]);

  // Persiste sempre que o tenant muda.
  useEffect(() => {
    if (user?.selectedTenantId) {
      localStorage.setItem(`${STORAGE_PREFIX}${user.sub}`, user.selectedTenantId);
    }
  }, [user?.selectedTenantId, user?.sub]);

  if (!user) return <>{children}</>; // login screens
  if (user.selectedTenantId) return <>{children}</>;

  // Sem tenant selecionado e nenhum disponível
  if (tenants.length === 0) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3, bgcolor: "background.default" }}>
        <Card sx={{ maxWidth: 480, p: 3, textAlign: "center" }}>
          <BusinessIcon color="error" sx={{ fontSize: 56, mb: 1 }} />
          <Typography variant="h6">Sem instituição vinculada</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Você não está associado a nenhuma instituição ativa. Solicite ao administrador o
            vínculo para continuar.
          </Typography>
        </Card>
      </Box>
    );
  }

  const handleChoose = async (id: string) => {
    setBusy(true);
    try { await switchTenant(id); } finally { setBusy(false); }
  };

  const renderCards = tenants.length <= 6;

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3, bgcolor: "background.default" }}>
      <Card sx={{ maxWidth: 720, width: "100%", p: 3 }}>
        <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <BusinessIcon color="primary" sx={{ fontSize: 48 }} />
          <Typography variant="h5">Selecione a instituição</Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {user.isMasterAdmin
              ? "Como master admin, você pode acessar qualquer instituição ativa. Sua escolha será salva como padrão."
              : "Você tem acesso a mais de uma instituição. Selecione com qual quer trabalhar — pode trocar depois pelo menu superior."}
          </Typography>
        </Stack>

        {renderCards ? (
          <Stack spacing={1.5}>
            {tenants.map((t) => (
              <Card key={t.id} variant="outlined">
                <CardActionArea onClick={() => handleChoose(t.id)} disabled={busy}>
                  <CardContent>
                    <Typography variant="subtitle1">{t.name}</Typography>
                    <Typography variant="caption" color="text.secondary">CNPJ: {formatCnpj(t.cnpj)}</Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <TextField
              select
              label="Instituição"
              value={pickerValue}
              onChange={(e) => setPickerValue(e.target.value)}
              fullWidth
              autoFocus
            >
              {tenants.map((t: AuthTenant) => (
                <MenuItem key={t.id} value={t.id}>{t.name} — {formatCnpj(t.cnpj)}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              size="large"
              disabled={!pickerValue || busy}
              onClick={() => pickerValue && handleChoose(pickerValue)}
            >
              Continuar
            </Button>
          </Stack>
        )}
      </Card>
    </Box>
  );
}

function formatCnpj(cnpj: string): string {
  const d = cnpj.replace(/\D/g, "");
  if (d.length !== 14) return cnpj;
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}
